import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { api } from "@/lib/api";
import { catalogueErrorMessage } from "../../lib/catalogueErrors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/LanguageContext";
import { toast } from "sonner";
import { Plus, Tag, Trash2, Save, X, Edit2, ToggleLeft, ToggleRight } from "lucide-react";
// RELATIVE paths on purpose (not "@/…"): these modules must be REALLY resolved under
// Jest, not mocked — the "@/" alias is not mapped on the Jest side (cf.
// lib/LanguageContext.jsx). A relative path resolves natively.
import TaxCategoryField from "../TaxCategoryField";
import useTaxProfile from "../../hooks/useTaxProfile";
import useFieldErrors from "../../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";
import { requiredField } from "../../lib/validation";
import defaultGatewayApi from "../../gateway/api";
import useTenantCurrency from "../../lib/useTenantCurrency";
import CatalogueCurrencyField, { isLegacyCurrency } from "../CatalogueCurrencyField";
import { formatMoneyCode } from "../../lib/formatMoney";
import { useEntitlements } from "../entitlements/EntitlementsContext";
import { CAP } from "../entitlements/capabilities";
import CapabilityNotice from "../entitlements/CapabilityNotice";

// NO currency list. The platform settles in exactly one currency and the money core
// refuses every other, so this form STATES it (CatalogueCurrencyField) rather than
// offering a choice — the CAD/USD/EUR/INR selector that used to sit here could only ever
// produce a service that was advertised and then refused at the buyer's checkout.

// Stripe's per-charge ceiling in the smallest currency unit. A price above it is storable
// and publishable today, and fails only at the customer's checkout.
const MAX_PRICE_CENTS = 99999999;

// H2-02/H2-16, converged onto the shared recipe (lib/validation.js). The price check
// stays a `superRefine` over the raw `_price_dollars` string rather than a `z.number()`
// field: the ceiling is THIS catalogue's own (Stripe's per-charge max), not a generic
// numeric rule, so it keeps its bespoke `bookingServices.priceOutOfRange` key (with the
// {max} it always carried) instead of folding into lib/validation.js's generic
// "numberInvalid" — a form-specific message stays owned by the form.
const SERVICE_SCHEMA = z.object({
  name: requiredField(200),
  _price_dollars: z.string(),
}).superRefine((val, ctx) => {
  const priceCents = Math.round(Number(val._price_dollars || 0) * 100);
  if (!Number.isFinite(priceCents) || priceCents < 0 || priceCents > MAX_PRICE_CENTS) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["_price_dollars"], message: "bookingServices.priceOutOfRange" });
  }
});

/**
 * ServicesAdmin — CRUD for the booking service catalogue (`booking_services`).
 *
 * This priced catalogue is what the weekly hours and extra hours offer: each service's
 * `duration_min` is the length of the appointment it books, and its price is read LIVE
 * at booking time (docs/booking-module/open-hours-design.md).
 * Money is entered in DOLLARS and stored in cents (`admin/money.js` owns the one
 * conversion — two forms here used to ask a human for cents, so typing 120.50 billed
 * CA$1.20).
 */
export default function ServicesAdmin({ gatewayApi = defaultGatewayApi }) {
  const t = useT("admin");
  const tc = useT("common");
  const tenantCurrency = useTenantCurrency();
  const taxProfile = useTaxProfile();
  const { fieldErrors, validate, clearFieldError, clearAllErrors } = useFieldErrors();
  // Making a service `video` is gated server-side on `care.video_rooms` (403 not in plan,
  // 503 not built). When the map says it is not usable, the option stays listed but is
  // disabled, with the reason beside it; while the map is unknown nothing is claimed.
  const ent = useEntitlements();
  const videoState = ent.status(CAP.videoRooms);
  const videoWithheld = videoState === "not_in_plan" || videoState === "coming_soon";
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  // Per-minute services are priced by `tenant_pricing` (rate · floor · cap · base fee),
  // in the SETTLEMENT currency. `price_cents` and `currency` are read by nothing on that
  // lane, so leaving the two fields live invited a practitioner to set "120.00 CAD" on a
  // service the patient is billed for in USD — a number they would then trust.
  //
  // NO FORM CAN SET THIS ANY MORE (the chooser above is `delivery_mode` now), but a
  // practice that already sells a per-minute consultation must still be able to OPEN its
  // service without the screen lying about where the price comes from. Read-only in
  // effect: `editing.pricing_mode` round-trips untouched on save.
  const isPerMinute = editing?.pricing_mode === "per_minute";

  const updateEditing = (patch) => {
    setEditing((e) => ({ ...e, ...patch }));
    Object.keys(patch).forEach(clearFieldError);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/booking/services");
      setServices(data || []);
    } catch (e) {
      setServices([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    clearAllErrors();
    // NO `currency`: the server denominates a new row in the platform's settlement
    // currency and keeps an existing row's on an edit (`resolved_currency`).
    setEditing({
      name: "",
      description: "",
      _price_dollars: "0.00",
      duration_min: 60,
      pricing_mode: "fixed",
      delivery_mode: "in_person",
      active: true,
      tax_category: "standard",
      exemption_citation: "",
      sac_code: "",
    });
  };

  const startEdit = (s) => {
    clearAllErrors();
    setEditing({
      ...s,
      _price_dollars: ((s.price_cents || 0) / 100).toFixed(2),
      tax_category: s.tax_category || "standard",
      exemption_citation: s.exemption_citation || "",
      sac_code: s.sac_code || "",
    });
  };

  const save = async () => {
    // H2-02 — `min="0"` on the input does not stop a TYPED `-50`, and the resulting 422
    // was what crashed the admin (a FastAPI `detail` array handed to React as a child).
    // The toast is no longer a crash, but a validation error the form can answer itself
    // should never become a round-trip. H2-16 is the same field's other end: Stripe
    // refuses any charge over 99,999,999 minor units, so a trailing-zeros typo
    // (`8000` for `800.00`) reaches the public marketplace and only fails at checkout,
    // after the customer has committed.
    if (validate(SERVICE_SCHEMA, editing)) { toast.error(tc("validation.fixHighlighted")); return; }
    const priceCents = Math.round(Number(editing._price_dollars || 0) * 100);
    setBusy(true);
    try {
      const payload = {
        name: editing.name,
        description: editing.description || "",
        price_cents: priceCents,
        // Only when the row holds one: the held currency on an edit (accepted as-is by
        // the server), or the settlement currency the practitioner switched to. A new
        // row sends none and is denominated by the platform.
        ...(editing.currency ? { currency: editing.currency } : {}),
        duration_min: parseInt(editing.duration_min || 60, 10),
        // ROUND-TRIPPED, not chosen: nothing on this form writes `pricing_mode` any more,
        // and sending "fixed" for a service that is per-minute would silently re-price a
        // practice's live consultations on the next unrelated edit.
        pricing_mode: editing.pricing_mode === "per_minute" ? "per_minute" : "fixed",
        delivery_mode: editing.delivery_mode === "video" ? "video" : "in_person",
        active: !!editing.active,
        tax_category: editing.tax_category || "standard",
        exemption_citation: editing.exemption_citation || "",
        sac_code: editing.sac_code || "",
      };
      if (editing.id) await api.put(`/booking/services/${editing.id}`, payload);
      else await api.post("/booking/services", payload);
      toast.success(t("bookingServices.toast.saved"));
      setEditing(null);
      load();
    } catch (e) {
      toast.error(catalogueErrorMessage(e, tc, t("bookingServices.toast.error")));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm(t("bookingServices.confirmDelete"))) return;
    try {
      const { data } = await api.delete(`/booking/services/${id}`);
      toast.success(data?.deactivated ? t("bookingServices.toast.deactivated") : t("bookingServices.toast.deleted"));
      load();
    } catch (e) {
      toast.error(catalogueErrorMessage(e, tc, t("bookingServices.toast.error")));
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/booking/services/${s.id}`, {
        name: s.name,
        description: s.description || "",
        price_cents: s.price_cents,
        pricing_mode: s.pricing_mode || "fixed",
        delivery_mode: s.delivery_mode || "in_person",
        ...(s.currency ? { currency: s.currency } : {}),
        duration_min: s.duration_min,
        active: !s.active,
        tax_category: s.tax_category || "standard",
        exemption_citation: s.exemption_citation || "",
        sac_code: s.sac_code || "",
      });
      load();
    } catch (e) {
      toast.error(catalogueErrorMessage(e, tc, t("bookingServices.toast.error")));
    }
  };

  return (
    <div data-testid="booking-services-admin" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3">
            <Tag className="w-6 h-6 text-sol-gold" /> {t("bookingServices.title")}
          </h1>
          <p className="text-eh-muted text-sm mt-1">{t("bookingServices.subtitle")}</p>
        </div>
        {!editing && (
          <Button onClick={startNew} data-testid="bsvc-new" className="bg-eh-ink text-eh-bg">
            <Plus className="w-4 h-4 mr-1" /> {t("bookingServices.new")}
          </Button>
        )}
      </div>

      {editing && (
        <div className="bg-white rounded-2xl border border-eh-border-soft p-6 space-y-3" data-testid="bsvc-editor">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t("bookingServices.nameLabel")}</Label>
              <Input
                value={editing.name}
                onChange={(e) => updateEditing({ name: e.target.value })}
                data-testid="bsvc-name"
                placeholder={t("bookingServices.namePlaceholder")}
                className={`mt-1.5 ${fieldErrors.name ? invalidInputClass : ""}`}
              />
              <FieldError message={fieldErrors.name ? tc(fieldErrors.name) : ""} testId="bsvc-name-error" />
            </div>
            <div>
              <Label className="text-xs">{t("bookingServices.durationLabel")}</Label>
              <Input type="number" min="5" max="600" value={editing.duration_min} onChange={(e) => setEditing({ ...editing, duration_min: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              {/* THE ONLY WRITER of `delivery_mode`, and — since the per-minute chooser
                  was withdrawn — the only mode control on this form. It answers WHERE the
                  appointment happens, which is a different question from how it is priced:
                  a video consultation is sold as a fixed price for a fixed block like any
                  other service, and it is this field, not the price, that gives it a room
                  and puts it under the telehealth commission rate.

                  The per-minute chooser that stood here is withdrawn from the UI, not from
                  the product: a practice that already sells one keeps selling it, and its
                  services keep `pricing_mode: "per_minute"` (which forces `video` at the
                  server's write boundary). Nothing in this form can create a new one. */}
              <Label className="text-xs" htmlFor="bsvc-delivery-mode">
                {t("bookingServices.deliveryModeLabel")}
              </Label>
              <select
                id="bsvc-delivery-mode"
                data-testid="bsvc-delivery-mode"
                value={editing.delivery_mode || "in_person"}
                onChange={(e) => updateEditing({ delivery_mode: e.target.value })}
                className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="in_person">{t("bookingServices.deliveryInPerson")}</option>
                {/* Disabled, not removed: an existing video service must still read as
                    video, never be silently re-labelled "in person". */}
                <option value="video" disabled={videoWithheld}>{t("bookingServices.deliveryVideo")}</option>
              </select>
              <p className="text-xs text-eh-muted mt-1">
                {editing.delivery_mode === "video"
                  ? t("bookingServices.deliveryVideoHint")
                  : t("bookingServices.deliveryInPersonHint")}
              </p>
              <div className="mt-2">
                <CapabilityNotice
                  state={videoState}
                  eta={ent.eta(CAP.videoRooms)}
                  body={t("bookingServices.videoPlanNotice")}
                  testId="bsvc-video-plan-notice"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">{t("bookingServices.priceLabel")}</Label>
              <Input
                type="number"
                min="0"
                max={MAX_PRICE_CENTS / 100}
                step="0.01"
                value={isPerMinute ? "" : editing._price_dollars}
                onChange={(e) => updateEditing({ _price_dollars: e.target.value })}
                data-testid="bsvc-price"
                placeholder="0.00"
                disabled={isPerMinute}
                className={`mt-1.5 ${isPerMinute ? "bg-eh-cream/60 text-eh-muted" : ""} ${fieldErrors._price_dollars ? invalidInputClass : ""}`}
              />
              {isPerMinute ? (
                <p className="text-xs text-eh-muted mt-1" data-testid="bsvc-price-ignored">
                  {t("bookingServices.priceIgnoredPerMinute")}
                </p>
              ) : (
                <FieldError
                  message={fieldErrors._price_dollars
                    ? t(fieldErrors._price_dollars, { max: (MAX_PRICE_CENTS / 100).toFixed(2) }, "Enter a price between 0 and {max}.")
                    : ""}
                  testId="bsvc-price-error"
                />
              )}
            </div>
            {/* STATED, not chosen; switchable only for a row held in a retired currency.
                Not rendered at all for a per-minute service: its money comes from
                `tenant_pricing`, in the settlement currency, and the price hint above
                already says so — a currency box here would be a value nothing reads. */}
            {!isPerMinute && (
              <CatalogueCurrencyField
                currency={editing.currency}
                denomination={tenantCurrency}
                onSwitch={(cur) => updateEditing({ currency: cur })}
                label={t("bookingServices.currencyLabel")}
                testid="bsvc"
              />
            )}
          </div>
          <div>
            <Label className="text-xs">{t("bookingServices.descriptionLabel")}</Label>
            <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} className="w-full border rounded-md p-2 text-sm mt-1.5" />
          </div>
          <TaxCategoryField
            value={{ tax_category: editing.tax_category, exemption_citation: editing.exemption_citation, sac_code: editing.sac_code }}
            onChange={(next) => setEditing({ ...editing, ...next })}
            country={taxProfile?.country}
          />
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="bsvc-active" />
            <span>{t("bookingServices.activeLabel")}</span>
          </label>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setEditing(null)}><X className="w-3 h-3 mr-1" /> {tc("actions.cancel")}</Button>
            <Button size="sm" onClick={save} disabled={busy} data-testid="bsvc-save" className="bg-eh-ink text-eh-bg">
              <Save className="w-3 h-3 mr-1" /> {tc("actions.save")}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {services.length === 0 && !editing && (
          <p className="text-center text-eh-muted text-sm py-6">{t("bookingServices.empty")}</p>
        )}
        {services.map((s) => (
          <div key={s.id} className={`rounded-xl p-4 flex items-center gap-4 border ${s.active ? "bg-white border-eh-border-soft" : "bg-eh-cream/40 border-transparent opacity-70"}`} data-testid={`bsvc-row-${s.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-eh-ink">{s.name}</div>
              {s.description && <div className="text-xs text-eh-muted truncate">{s.description}</div>}
              {/* A per-minute service's `price_cents` is legitimately 0 — its money
                  comes from `tenant_pricing` — so the old row rendered it as "Free",
                  indistinguishable from a genuinely free fixed service and with nothing
                  anywhere naming the lane. */}
              <div className="text-xs text-eh-muted mt-0.5 flex items-center gap-2 flex-wrap">
                <span>
                  {s.duration_min} min · {s.pricing_mode === "per_minute"
                    ? t("bookingServices.perMinuteBadge")
                    : s.price_cents > 0
                      ? formatMoneyCode(s.price_cents, s.currency)
                      : t("bookingServices.free")}
                </span>
                {/* WHERE it happens, said on the row, because it is the difference
                    between an hour the client travels to and one they open a link for —
                    and because it is what decides the commission the practice pays. */}
                {s.delivery_mode === "video" && (
                  <span className="px-2 py-0.5 rounded-full bg-eh-cream text-eh-ink" data-testid={`bsvc-video-${s.id}`}>
                    {t("bookingServices.deliveryVideo")}
                  </span>
                )}
                {s.pricing_mode !== "per_minute" && isLegacyCurrency(s.currency, tenantCurrency) && (
                  <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700" data-testid={`bsvc-legacy-currency-${s.id}`}>
                    {t("catalogue.currency.notPayable", { held: s.currency.toUpperCase() })}
                  </span>
                )}
              </div>
            </div>
            <button onClick={() => toggleActive(s)} title={s.active ? t("bookingServices.deactivate") : t("bookingServices.activate")} className="p-1.5 hover:bg-eh-cream rounded">
              {s.active ? <ToggleRight className="w-5 h-5 text-emerald-600" /> : <ToggleLeft className="w-5 h-5 text-eh-muted" />}
            </button>
            <button onClick={() => startEdit(s)} className="p-1.5 hover:bg-eh-cream rounded" data-testid={`bsvc-edit-${s.id}`}><Edit2 className="w-4 h-4" /></button>
            <button onClick={() => del(s.id)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded" data-testid={`bsvc-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
